import * as TabsPrimitive from '@radix-ui/react-tabs'
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef } from 'react'

import { cn } from '@/utils/cn'
import { type VariantProps, cva } from 'class-variance-authority'

export const Tabs = TabsPrimitive.Root

const tabsListVariants = cva('inline-flex items-center ring-inset has-[:focus-visible]:ring', {
  variants: {
    variant: {
      pill: 'items-stretch rounded-full border bg-surface p-1 has-[:focus-visible]:border-focus',
      line: 'gap-0 rounded-t-xl border-b ring-inset has-[:focus-visible]:border-focus',
    },
  },
  defaultVariants: {
    variant: 'pill',
  },
})

export const TabsList = forwardRef<
  ElementRef<typeof TabsPrimitive.List>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List> & VariantProps<typeof tabsListVariants>
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.List ref={ref} className={cn(tabsListVariants({ variant }), className)} {...props} />
))
TabsList.displayName = TabsPrimitive.List.displayName

const tabVariants = cva(
  cn(
    'inline-flex items-center justify-center outline-none',
    'whitespace-nowrap font-medium text-sm',
    'text-body-foreground transition-all',
    'hocus:text-active-foreground',
    'disabled:cursor-not-allowed disabled:text-disabled-foreground',
    'disabled:hocus:text-disabled-foreground'
  ),
  {
    variants: {
      variant: {
        pill: cn(
          'h-full min-w-0 flex-grow rounded-full px-2 ',
          'hocus:text-active-foreground',
          'data-[state=active]:bg-page data-[state=active]:text-active-foreground',
          'disabled:data-[state=active]:bg-input-disabled'
        ),
        line: cn(
          '-mb-px border-transparent border-b-[3px] px-4 py-2 text-body-foreground',
          'data-[state=active]:border-focus data-[state=active]:text-active-foreground',
          'disabled:data-[state=active]:border-input-disabled'
        ),
      },
    },
    defaultVariants: {
      variant: 'pill',
    },
  }
)

export const TabsTrigger = forwardRef<
  ElementRef<typeof TabsPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & VariantProps<typeof tabVariants>
>(({ className, variant = 'pill', ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(tabVariants({ variant, className }))}
    data-variant={variant}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

export const TabsContent = forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    tabIndex={-1}
    className={cn('outline-none data-[state=inactive]:hidden', className)}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName
